import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { Recipe } from '../models/Recipe';

export interface IngredientStatus {
  ingredient: string;
  available: boolean;
  substitute?: string;
}

interface IngredientCheckDialogProps {
  open: boolean;
  onClose: () => void;
  recipe: Recipe;
  onConfirm: (availableIngredients: IngredientStatus[], notes: string) => void;
  initialCheckedStatus?: Record<string, boolean>;
}

const IngredientCheckDialog: React.FC<IngredientCheckDialogProps> = ({
  open,
  onClose,
  recipe,
  onConfirm,
  initialCheckedStatus = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Parse ingredients from recipe
  const parsedIngredients = useMemo(() => {
    if (!recipe.ingredients) {
      return [];
    }
    // Split by newlines only (preserve commas in ingredient descriptions)
    return recipe.ingredients
      .split('\n')
      .map(i => i.trim())
      .filter(i => i.length > 0);
  }, [recipe.ingredients]);

  // State for ingredient availability - use initialCheckedStatus if provided
  const [ingredientStatus, setIngredientStatus] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    parsedIngredients.forEach(ing => {
      // Use initialCheckedStatus if available, otherwise default to true
      initial[ing] = initialCheckedStatus[ing] ?? true;
    });
    return initial;
  });

  // State for user notes
  const [notes, setNotes] = useState('');

  // Update ingredient status when initialCheckedStatus changes or dialog opens
  React.useEffect(() => {
    if (open && Object.keys(initialCheckedStatus).length > 0) {
      const updated: Record<string, boolean> = {};
      parsedIngredients.forEach(ing => {
        updated[ing] = initialCheckedStatus[ing] ?? true;
      });
      setIngredientStatus(updated);
    }
  }, [open, initialCheckedStatus, parsedIngredients]);

  const handleToggleIngredient = (ingredient: string) => {
    setIngredientStatus(prev => ({
      ...prev,
      [ingredient]: !prev[ingredient],
    }));
  };

  const handleConfirm = () => {
    const statuses: IngredientStatus[] = parsedIngredients.map(ing => ({
      ingredient: ing,
      available: ingredientStatus[ing] ?? true,
    }));
    onConfirm(statuses, notes);
  };

  const unavailableCount = parsedIngredients.filter(ing => !ingredientStatus[ing]).length;
  const allAvailable = unavailableCount === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      sx={{
        zIndex: 1400,
        '& .MuiDialog-container': {
          alignItems: isMobile ? 'flex-start' : 'center',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" component="span">
            Check Your Ingredients
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {recipe.title}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Uncheck any ingredients you don't have. Otto will adapt the recipe for you.
          </Typography>

          {/* Ingredient checklist */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {parsedIngredients.map((ingredient, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={ingredientStatus[ingredient] ?? true}
                    onChange={() => handleToggleIngredient(ingredient)}
                    color="primary"
                  />
                }
                label={
                  <Typography
                    sx={{
                      textDecoration: ingredientStatus[ingredient] ? 'none' : 'line-through',
                      color: ingredientStatus[ingredient] ? 'text.primary' : 'text.disabled',
                    }}
                  >
                    {ingredient}
                  </Typography>
                }
                sx={{
                  mx: 0,
                  py: 0.5,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              />
            ))}
          </Box>

          {/* Status summary */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            {allAvailable ? (
              <Chip
                icon={<CheckCircleIcon />}
                label="All ingredients available"
                color="success"
                size="small"
              />
            ) : (
              <Chip
                icon={<WarningIcon />}
                label={`${unavailableCount} ingredient${unavailableCount > 1 ? 's' : ''} missing`}
                color="warning"
                size="small"
              />
            )}
          </Box>
        </Box>

        <Divider />

        {/* Notes section */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Any special requests? (optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            placeholder="E.g., 'I'm in a hurry', 'Making it vegetarian', 'Using an air fryer instead of oven'"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            variant="outlined"
            size="small"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
        >
          {unavailableCount > 0 || notes.trim()
            ? 'Customize & Start Cooking'
            : 'Start Cooking'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(IngredientCheckDialog);
